/*
  Millie & Jones: express checkout and the delivery date (v18, 12 Oct 2026). No dependencies beyond
  assets/mj-delivery-estimate.js (window.MJETA, loaded at the end of every page).

  Products on the basket's date picker (one of their collections has custom.date_picker with the picker on)
  reached checkout without a delivery date whenever the shopper skipped the basket: Apple Pay, Shop Pay or
  Google Pay on the product page, or the mattress pop-up's Checkout link. This gives those routes the date the
  basket's picker would have picked for them (MJETA.firstBookable), so the order confirmation says "Arriving ...".

  1. Product page. Shopify's express buttons buy the product alone and read the product form, so
     MJExpress.product() keeps a hidden properties[Delivery Date] input in that form ("13 Oct 2026", the
     picker's line format) and a note under the buttons. The date is worked out again every minute, at 12:00 UK
     time (the cutoff), when the tab comes back and when an express button is pressed.
  2. "Added to your basket" pop-ups (bed page: assets/mj-bed.js; mattress page: assets/add-on-drawer-new.js).
     The add asks for sections/mj-express-buttons.liquid, which brings the basket's own express buttons
     (Apple Pay, Shop Pay, Google Pay, PayPal: the whole basket, so the mattress, protector and assembly come
     too) and the basket's date rules, worked out by the same Liquid as the basket page. MJExpress.afterAdd()
     then gives the basket what its picker would hold: the cart attribute "Delivery Date" (2026-10-13) and the
     line property "Delivery Date" on every line (13 Oct 2026), and only then shows the buttons. When the basket
     needs something only the basket page does (a weekend charge to add or remove), or a request fails, the
     buttons stay hidden and "View basket" does the rest. At 12:00 an open pop-up checks the date again.
     When the basket was empty, the date goes on the lines with the add itself (MJExpress.predict), so the usual
     case costs one extra request (the attribute).
  3. MJExpress.ensure() does the same check on its own, within a time limit: the mattress pop-up's Checkout
     link runs it and goes to the basket when it can't be sure of the date.
  Checks run one at a time, so two quick adds can't write over each other, and every request and every check has
  a time limit; a check that runs out of time makes no further writes.
  A date already in the basket is kept while it is still bookable, unless this file picked that very date for the
  shopper in this tab (sessionStorage mjAutoDate) and the picker's flag still says it was picked for them
  (cartDeliveryDateAuto = '1'): then it moves to the earliest date, as the picker itself does.
  The device clock is corrected from the store's Date header when it is more than a minute out (MJETA.setClock).

  v21.2 (17 Oct 2026): both "Added to your basket" pop-ups have Checkout and View basket side by side again,
  then the express buttons, then Keep shopping as a text link.
  4. Checkout (a[data-mj-checkout]) runs ensure() and goes to checkout, or to the basket when it can't be sure of
     the date (a weekend charge to add or remove, a request that fails or runs out of time). Its href is the
     basket, so a link opened in a new tab, or a page where this file didn't load, still lands somewhere safe.
     The date note sits under the two buttons and shows whenever the basket has its date, express buttons or not.
  5. Taps in the pop-ups are counted (MJExpress.shown(dialog, page) when one opens): Microsoft Clarity custom
     events popup_shown, popup_checkout, popup_view_basket, popup_keep_shopping, popup_close, popup_dismiss
     (closed by the backdrop or Esc), popup_checkout_to_basket, popup_checkout_new_tab (Checkout with Ctrl or
     Cmd, which opens the basket) and popup_wallet_<shoppay|applepay|googlepay|paypal|...>, plus a Shopify
     customer event "mj_popup" { action, page, wallet } for the MJ custom pixel.
*/
(function () {
  'use strict';
  if (window.MJExpress) return;

  var SECTION = 'mj-express-buttons';
  var PROP = 'Delivery Date';
  var AUTO_KEY = 'cartDeliveryDateAuto';
  var WALLETS = 'shopify-accelerated-checkout-cart';

  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  // MJETA loads last on the page, after the product page scripts. Resolves true once it is there,
  // false if the page finished loading without it.
  function whenRules() {
    return new Promise(function (resolve) {
      if (window.MJETA) { resolve(true); return; }
      if (document.readyState === 'complete') { resolve(false); return; }
      document.addEventListener('DOMContentLoaded', function () { if (window.MJETA) resolve(true); });
      window.addEventListener('load', function () { resolve(!!window.MJETA); });
    });
  }
  function withTimeout(promise, ms, fallback, onTimeout) {
    return new Promise(function (resolve) {
      var done = false;
      var timer = null;
      var finish = function (value) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(value);
      };
      timer = setTimeout(function () {
        if (onTimeout) { try { onTimeout(); } catch (e) { /* nothing */ } }
        finish(fallback);
      }, ms);
      promise.then(finish, function () { finish(fallback); });
    });
  }
  // fetch with a time limit (8 seconds): a request that hangs is abandoned
  function request(url, init) {
    init = init || {};
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    if (ctl) init.signal = ctl.signal;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, 8000);
    return fetch(url, init).then(function (r) { clearTimeout(timer); return r; }, function (e) { clearTimeout(timer); throw e; });
  }
  function readJSON(el) {
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }

  // The store's clock: the Date header of a basket read
  function noteClock(response) {
    try {
      var server = Date.parse(response.headers.get('Date'));
      if (!isNaN(server) && window.MJETA && window.MJETA.setClock) window.MJETA.setClock(server - Date.now());
    } catch (e) { /* keep the device clock */ }
  }
  function getCart() {
    return request(root() + 'cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart');
        noteClock(r);
        return r.json();
      });
  }
  function post(url, body, token) {
    if (token && token.cancelled) return Promise.reject(new Error('cancelled'));
    return request(root() + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) { var e = new Error(url); e.status = r.status; throw e; }
      return r.json();
    });
  }
  // The basket picker's flag: '1' it picked the date for the shopper in this tab, '0' they chose it. Moving a
  // date also needs it to be the very date this file picked here (a date chosen in another tab stays).
  var MINE_KEY = 'mjAutoDate';
  function tracking(savedIso) {
    try { return sessionStorage.getItem(AUTO_KEY) === '1' && !!savedIso && sessionStorage.getItem(MINE_KEY) === savedIso; } catch (e) { return false; }
  }
  function setAuto(isoValue) {
    try {
      sessionStorage.setItem(AUTO_KEY, '1');
      sessionStorage.setItem(MINE_KEY, isoValue);
    } catch (e) { /* private mode */ }
  }

  // At 12:00 UK time the earliest date moves on a day
  function atCutoff(fn) {
    if (!window.MJETA || !window.MJETA.msToCutoff) return;
    var ms = window.MJETA.msToCutoff();
    if (!(ms > 0)) return;
    setTimeout(fn, Math.min(ms, 2147483000));
  }

  /* ---------- 1. Product page ---------- */

  // The product's date rules (snippets/mj-date-rules.liquid) and one variant: the earliest bookable date, or null
  function productDate(rules, variantId) {
    if (!rules || !rules.on || !window.MJETA) return null;
    var held = (rules.held || []).map(String).indexOf(String(variantId)) !== -1;
    return window.MJETA.firstBookable({ blocked: rules.blocked, restock: held ? rules.restock : '', sat: rules.sat, sun: rules.sun });
  }
  function rulesIn(scope) {
    return readJSON((scope || document).querySelector('script[data-mj-date-rules]'));
  }
  function writeNote(note, date, tail, lead) {
    note.textContent = '';
    note.appendChild(document.createTextNode(lead || 'Express checkout books delivery for '));
    var b = document.createElement('strong');
    b.textContent = window.MJETA.long(date);
    note.appendChild(b);
    note.appendChild(document.createTextNode(tail));
  }

  // opts: form (the product form), rules, note (element under the express buttons), express (the express
  // buttons' wrapper; the note shows only while it does), variant (function returning { id, available } or null)
  function product(opts) {
    var form = opts.form, rules = opts.rules, note = opts.note;
    if (!form || !rules || !rules.on) {
      if (note) note.hidden = true;
      return { update: function () {} };
    }
    var input = form.querySelector('input[name="properties[' + PROP + ']"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + PROP + ']';
      input.disabled = true;
      form.appendChild(input);
    }
    var started = false;
    function update() {
      if (!window.MJETA) return;
      started = true;
      var v = opts.variant ? opts.variant() : null;
      var d = v && v.available ? productDate(rules, v.id) : null;
      input.disabled = !d;
      input.value = d ? window.MJETA.lineFormat(d) : '';
      if (!note) return;
      var visible = !!d && !(opts.express && opts.express.hidden);
      note.hidden = !visible;
      if (visible) writeNote(note, d, '. For another day, add to basket and choose.');
    }
    function onCutoff() { update(); atCutoff(onCutoff); }
    setInterval(function () { if (started) update(); }, 60000);
    document.addEventListener('visibilitychange', function () { if (!document.hidden && started) update(); });
    if (opts.express) {
      ['pointerdown', 'touchstart', 'keydown', 'click'].forEach(function (type) {
        opts.express.addEventListener(type, function () { if (started) update(); }, { capture: true, passive: true });
      });
    }
    whenRules().then(function (ok) {
      if (!ok) return;
      update();
      atCutoff(onCutoff);
    });
    return { update: function () { if (window.MJETA) update(); } };
  }

  /* ---------- 2. After an add ---------- */

  var cartState = null; // the basket as last read on this page, for predict()
  function readCart() { return getCart().then(function (c) { cartState = c; }, function () { cartState = null; }); }
  function watchCart() {
    setTimeout(readCart, 300);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) readCart(); });
  }
  function setCart(cart) { cartState = cart && cart.items ? cart : null; }

  // The date to send with the lines being added, when the basket is empty (so nothing needs rewriting after).
  // Used once: until the basket is read again, the next add sends none and afterAdd() sorts its lines out.
  function predict(rules, variantId) {
    if (!window.MJETA || !cartState || cartState.item_count !== 0) return null;
    var d = productDate(rules, variantId);
    if (!d) return null;
    cartState = null;
    return window.MJETA.lineFormat(d);
  }
  function predictFromPage(variantId) { return predict(rulesIn(document), variantId); }

  function parseSection(html) {
    if (!html || typeof DOMParser === 'undefined') return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var data = readJSON(doc.querySelector('script[data-mj-cart-date]'));
    var buttons = doc.querySelector('[data-mj-express-buttons]');
    return data ? { data: data, buttons: buttons ? buttons.innerHTML : '' } : null;
  }
  function fetchSection() {
    return request(root() + '?sections=' + SECTION, { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('section'); return r.json(); })
      .then(function (j) { return j && j[SECTION]; });
  }

  // One check at a time
  var chain = Promise.resolve();
  var running = 0;
  function queue(fn) {
    running++;
    var run = chain.then(fn, fn);
    chain = run.then(function () { running--; }, function () { running--; });
    return run;
  }

  // Every line the same "Delivery Date", one /cart/change.js at a time, reading the basket in between
  // (the picker's runLinePropertySync: a change can rebuild or reorder lines)
  function syncLines(value, token) {
    var guard = 0, misses = 0;
    function step() {
      if (++guard > 30 || (token && token.cancelled)) return Promise.resolve(false);
      return getCart().then(function (cart) {
        var pending = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (((cart.items[i].properties || {})[PROP] || '') !== value) { pending = cart.items[i]; break; }
        }
        if (!pending) return true;
        var props = {};
        Object.keys(pending.properties || {}).forEach(function (k) { props[k] = pending.properties[k]; });
        props[PROP] = value;
        return post('cart/change.js', { id: pending.key, quantity: pending.quantity, properties: props }, token)
          .then(step, function (e) {
            if ((e.status === 400 || e.status === 404) && misses < 3) { misses++; return step(); }
            throw e;
          });
      });
    }
    return step().catch(function () { return false; });
  }

  // Gives the basket the date its picker would hold. g: the section's data (sections/mj-express-buttons.liquid).
  // Resolves { ok, date }: ok when checkout from here carries the right date, or the basket needs none.
  function reconcile(g, token) {
    var M = window.MJETA;
    if (!g || !M) return Promise.resolve({ ok: false });
    var lines = g.lines || [];
    if (!g.wants) {
      // No picker for this basket. A date left from earlier is cleared by the basket page first.
      var stale = !!g.saved || !!g.surchargeDue || !!g.surcharge || lines.some(function (v) { return !!v; });
      return Promise.resolve({ ok: !stale, date: null });
    }
    var o = { now: M.storeNow(g.storeDate, g.storeHour) || undefined, blocked: g.blocked, restock: g.restock, sat: g.sat, sun: g.sun };
    var earliest = M.firstBookable(o);
    if (!earliest) return Promise.resolve({ ok: false });
    var saved = M.parseISO(g.saved);
    var target = saved && M.isBookable(saved, o) && !tracking(g.saved) ? saved : earliest;
    var weekend = target.getDay() === 0 || target.getDay() === 6;
    if (weekend !== !!g.surcharge) return Promise.resolve({ ok: false }); // a weekend charge to add or remove
    var value = M.lineFormat(target);
    var isoValue = M.iso(target);
    var linesOk = lines.every(function (v) { return v === value; });
    return (linesOk ? Promise.resolve(true) : syncLines(value, token)).then(function (synced) {
      if (!synced) return { ok: false };
      var attrs = {};
      if (g.saved !== isoValue) attrs[PROP] = isoValue;
      if (g.surchargeDue && !weekend) attrs['Weekend Surcharge Due'] = '';
      if (!Object.keys(attrs).length) return { ok: true, date: target };
      return post('cart/update.js', { attributes: attrs }, token).then(function () {
        if (target === earliest && (!g.saved || tracking(g.saved))) setAuto(isoValue);
        return { ok: true, date: target };
      });
    }).catch(function () { return { ok: false }; });
  }

  function walletsReady() {
    if (!window.customElements) return Promise.resolve(false);
    if (window.customElements.get(WALLETS)) return Promise.resolve(true);
    return withTimeout(window.customElements.whenDefined(WALLETS).then(function () { return true; }), 3000, false);
  }
  function isShown(el) { return !!(el && el.isConnected && el.getClientRects().length); }

  // PayPal opens its own window and lays an overlay over the page ("Click to continue"). Under a modal
  // <dialog> (the bed page pop-up) that overlay would sit beneath the dialog, so the dialog steps aside.
  function watchPayPal(dialog) {
    if (!dialog || dialog._mjPayPal || !('MutationObserver' in window)) return;
    dialog._mjPayPal = true;
    new MutationObserver(function (records) {
      if (!dialog.open) return;
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var n = records[i].addedNodes[j];
          if (n.nodeType === 1 && (/^paypal-overlay/.test(n.id || '') || (n.classList && n.classList.contains('paypal-checkout-sandbox')))) {
            // v21.2: PayPal's overlay means PayPal was pressed, even if its frame hid the tap
            walletCount(dialog, 'paypal', 15000);
            dialog.close();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true });
  }

  // box: { root, wallets, note } in the pop-up. html: the section as rendered with the add (fetched when it
  // is missing, or when another check was still running, so this one reads the basket as it is now).
  function afterAdd(html, box) {
    var fresh = running === 0;
    var token = { cancelled: false };
    if (box && box.root) box.root.hidden = true;
    if (box && box.note) box.note.hidden = true;
    return queue(function () {
      return whenRules()
        .then(function (ok) {
          if (!ok) throw new Error('rules');
          return withTimeout((fresh && html) ? Promise.resolve(html) : fetchSection(), 8000, null);
        })
        .then(function (h) {
          var s = parseSection(h);
          if (!s) return { ok: false };
          return withTimeout(reconcile(s.data, token), 15000, { ok: false }, function () { token.cancelled = true; }).then(function (res) {
            // v21.2: the note is for Checkout and the express buttons alike, so it shows whenever the basket has its date
            if (box && box.note && res.ok && res.date) {
              writeNote(box.note, res.date, '. To change the day, view your basket.', 'Checkout books delivery for ');
              box.note.hidden = false;
            }
            // at 12:00 the earliest date moves on: check again while the pop-up is still open
            if (box && res.ok && res.date) atCutoff(function () { if (isShown(box.note) || isShown(box.root)) afterAdd(null, box); });
            if (!res.ok || !s.buttons || !box || !box.root || !box.wallets || s.buttons.indexOf(WALLETS) === -1) return res;
            return walletsReady().then(function (ready) {
              if (!ready) return res;
              box.wallets.innerHTML = s.buttons;
              box.root.hidden = false;
              watchPayPal(box.root.closest && box.root.closest('dialog'));
              return res;
            });
          });
        })
        .then(function (res) {
          // the basket has lines now: the next add on this page sends no date with them
          readCart();
          return res;
        });
    }).catch(function () { return { ok: false }; });
  }

  // For a Checkout link that skips the basket: resolves { ok } within ms (default 6 seconds)
  function ensure(ms) {
    ms = ms || 6000;
    var token = { cancelled: false };
    var run = queue(function () {
      if (token.cancelled) return { ok: false };
      return withTimeout(whenRules()
        .then(function (ok) { if (!ok) throw new Error('rules'); return fetchSection(); })
        .then(function (h) { var s = parseSection(h); return s ? reconcile(s.data, token) : { ok: false }; }), ms, { ok: false }, function () { token.cancelled = true; });
    }).catch(function () { return { ok: false }; });
    return withTimeout(run, ms, { ok: false }, function () { token.cancelled = true; });
  }

  /* ---------- 4 and 5. The pop-ups' Checkout, and counting taps (v21.2) ---------- */

  // Clarity custom event and tag, and a Shopify customer event. Counting must never get in the way of a tap.
  function count(action, page, wallet) {
    var name = action + (wallet ? '_' + wallet : '');
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', 'popup_' + name);
        window.clarity('set', 'popup', name);
      }
    } catch (e) { /* nothing */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('mj_popup', { action: action, page: page || '', wallet: wallet || '' });
      }
    } catch (e) { /* nothing */ }
  }
  // The pop-up an element is in, once MJExpress.shown() has been told about it
  function popOf(el) {
    var dialog = el && el.closest ? el.closest('dialog') : null;
    return dialog && dialog._mjPop ? dialog : null;
  }
  function tap(dialog, action, wallet) {
    var st = dialog && dialog._mjPop;
    if (!st) return;
    st.acted = true;
    count(action, st.page, wallet);
  }

  // Called each time a pop-up opens. page: 'bed', 'mattress' or 'product'.
  function shown(dialog, page) {
    if (!dialog) return;
    var st = dialog._mjPop;
    if (!st) {
      st = dialog._mjPop = { page: page || '', acted: false, wallet: '', walletAt: 0 };
      // Closed without a tap on any of its buttons: the backdrop or Esc
      dialog.addEventListener('close', function () {
        if (!st.acted) count('dismiss', st.page);
        st.acted = true;
      });
    }
    st.page = page || st.page;
    st.acted = false;
    st.wallet = '';
    st.walletAt = 0;
    count('shown', st.page);
  }

  // Checkout: the basket first gets the date its picker would hold (ensure), then checkout; the basket when
  // that can't be done. A click with a modifier key, or the middle button, opens the link's own href (the basket).
  function goCheckout(link, e, dialog) {
    // Already on its way: a second tap does nothing (and isn't counted twice)
    if (link.getAttribute('aria-busy') === 'true') {
      e.preventDefault();
      return;
    }
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      tap(dialog, 'checkout_new_tab'); // the browser opens the link's own address, the basket
      return;
    }
    tap(dialog, 'checkout');
    e.preventDefault();
    var basket = link.getAttribute('href') || root() + 'cart';
    var checkout = link.getAttribute('data-mj-checkout') || root() + 'checkout';
    link.setAttribute('aria-busy', 'true');
    if (!link.hasAttribute('data-mj-label')) link.setAttribute('data-mj-label', link.textContent);
    link.textContent = 'Opening…';
    ensure(6000).then(function (res) {
      // Still here 10 seconds later (the shopper stopped the page loading): the button works again
      setTimeout(function () { resetCheckout(link); }, 10000);
      if (res && res.ok) {
        window.location.assign(checkout);
        return;
      }
      tap(dialog, 'checkout_to_basket');
      window.location.assign(basket);
    });
  }
  function resetCheckout(link) {
    if (link.getAttribute('aria-busy') !== 'true') return;
    link.removeAttribute('aria-busy');
    if (link.hasAttribute('data-mj-label')) link.textContent = link.getAttribute('data-mj-label');
  }
  // Back from checkout to a page the browser kept: the button works again
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-mj-checkout][aria-busy="true"]'), resetCheckout);
  });

  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-mj-pop]') : null;
    var dialog = el && popOf(el);
    if (!dialog) return;
    var action = el.getAttribute('data-mj-pop');
    if (action === 'checkout' && el.hasAttribute('data-mj-checkout')) goCheckout(el, e, dialog);
    else tap(dialog, action);
  }, true);

  // Express buttons in a pop-up: which one. Each is an element with slot="button-<Wallet>" (button-ShopPay,
  // button-ApplePay, button-GooglePay, button-PayPal), counted once however the tap arrives.
  function inWallets(el) { return el && el.closest ? el.closest('[data-mjb-added-wallets], [data-mj-express-wallets]') : null; }
  // Counts a wallet once: the same one again within `within` ms (default 2 seconds) is the same tap
  function walletCount(dialog, name, within) {
    var st = dialog && dialog._mjPop;
    if (!st) return;
    var now = Date.now();
    if (st.wallet === name && now - st.walletAt < (within || 2000)) return;
    st.wallet = name;
    st.walletAt = now;
    tap(dialog, 'wallet', name);
  }
  function walletTap(dialog, el, within) {
    var slot = el && el.closest ? el.closest('[slot^="button-"]') : null;
    var name = slot ? String(slot.getAttribute('slot')).slice(7).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
    walletCount(dialog, name || 'other', within);
  }
  var lastTab = 0;
  function onWalletEvent(e) {
    if (e.type === 'keydown') {
      if (e.key === 'Tab') { lastTab = Date.now(); return; }
      if (e.key !== 'Enter' && e.key !== ' ') return;
    }
    var w = inWallets(e.target);
    var dialog = w && popOf(w);
    if (dialog) walletTap(dialog, e.target);
  }
  document.addEventListener('pointerdown', onWalletEvent, true);
  document.addEventListener('keydown', onWalletEvent, true);
  // A button drawn in its own frame (PayPal) takes the focus without the page seeing the tap. The focus is then
  // on the frame, or on the button's own element when the frame is inside it. Tabbing into it isn't a tap.
  window.addEventListener('blur', function () {
    setTimeout(function () {
      if (Date.now() - lastTab < 500) return;
      var el = document.activeElement;
      var w = inWallets(el);
      var dialog = w && popOf(w);
      // 10 seconds: a wallet's own window (Shop Pay) also takes the focus, after the tap was already counted
      if (dialog) walletTap(dialog, el, 10000);
    }, 0);
  });

  // Product pages with date rules read the basket once settled, for predict() and the clock
  function boot() { if (document.querySelector('script[data-mj-date-rules]')) watchCart(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.MJExpress = {
    SECTION: SECTION,
    product: product, rulesIn: rulesIn, productDate: productDate,
    watchCart: watchCart, setCart: setCart, predict: predict, predictFromPage: predictFromPage,
    afterAdd: afterAdd, ensure: ensure, reconcile: reconcile, parseSection: parseSection,
    shown: shown
  };
})();
